import { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, request as tokenRequest } from '../auth/authConfig';
import translationsProvider from 'translations/translationsProvider';
import translationKeys from 'translationKeys';

class AuthenticationService {
	private msalInstance: PublicClientApplication;
	private accessToken: string;
	private subscribers = [];

	constructor() {
		if (process.env.TS_JEST === '1') {
			return;
		}
		if(!msalConfig.auth.redirectUri){
			msalConfig.auth.redirectUri = window.location.origin;
		}
		this.msalInstance = new PublicClientApplication(msalConfig);
		this.msalInstance.initialize().then(() => {
			this.msalInstance.handleRedirectPromise()
				.then(this.handleTokenResponse)
				.catch((error) => {
					console.error('Error handling redirect promise:', error);
					// Handle error appropriately, e.g., notify user or retry
				});
		}).catch((error) => {
			console.error('Error initializing MSAL instance:', error);
			// Handle error appropriately, e.g., notify user or retry
		});
	}

	get instance() {
		return this.msalInstance;
	}

	get token() {
		return this.accessToken;
	}

	subscribe(subscriber: (token: string) => void) {
		this.subscribers.push(subscriber);
	}

	notifyTokenChange() {
		this.subscribers.map((s) => s(this.accessToken));
	}

	requestAccessToken = async (response?: Response): Promise<void> => {
		if (process.env.TS_JEST === '1') {
			this.accessToken = 'test_token';
			this.notifyTokenChange();
			return;
		}
		const accounts = this.msalInstance.getAllAccounts();

		const request = {
			...tokenRequest,
			account: accounts[0]
		};

		if (response) {
			if (response.status === 401 && response.headers.get('www-authenticate')) {
				let challenge = this.parseChallenge(response.headers.get('www-authenticate'));
				if (challenge['proposedAction'] === 'consent') {
					request.prompt = 'consent';
				}
				await this.msalInstance.acquireTokenRedirect(request);
			}
		} else {
			// Silently acquires an access token which is then attached to a request for Microsoft Graph data
			try {
				await this.msalInstance.acquireTokenSilent(request)
					.then(this.handleTokenResponse);
			} catch (e) {
				this.msalInstance.acquireTokenRedirect(request);
			}
		}
	};

	private handleTokenResponse = async (result: AuthenticationResult) => {
		if (result) {
			this.accessToken = result.accessToken;
			this.notifyTokenChange();
		}
	};

	private parseChallenge(challenge: string): { [key: string]: string } {
		// This regex checks the structure of the whole challenge header.  The complete
		// header needs to be checked for validity before we can be certain that
		// we will succeed in pulling out the individual parts.
		var bearerChallengeStructureValidation = /^\s*Bearer\s+([^,\s="]+?)="([^"]*?)"\s*(,\s*([^,\s="]+?)="([^"]*?)"\s*)*$/;

		// This regex pulls out the key and value from the very first pair.
		var firstKeyValuePairRegex = /^\s*Bearer\s+([^,\s="]+?)="([^"]*?)"\s*/;

		// This regex is used to pull out all of the key value pairs after the first one.
		// All of these begin with a comma.
		var allOtherKeyValuePairRegex = /(?:,\s*([^,\s="]+?)="([^"]*?)"\s*)/g;

		if (!bearerChallengeStructureValidation.test(challenge)) {
			throw new Error(translationsProvider.staticTranslate(translationKeys.AuthService.ParseChallenge.Error));
		}

		var challengeParameters = {};
		for (var match = firstKeyValuePairRegex.exec(challenge);
			match;
			match = allOtherKeyValuePairRegex.exec(challenge)) {

			challengeParameters[match[1]] = match[2];
		}

		return challengeParameters;
	}
}

export const AuthService = new AuthenticationService();
